import type { Tutorial } from '../../shared/types.js';

export const launchAgentTutorial: Tutorial = {
  name: 'launch-agent',
  title: 'Launch an agent',
  description:
    'Go from idea to a running agent in one dialog: describe a task, lean on the smart defaults, add any context, and launch.',
  category: 'agent-sessions',
  docPages: ['concepts/agents'],
  agenda: [
    'Describe the task you want done',
    'Pick the agent, model, and provider key',
    'Add context and fine-tune under Advanced',
    'Launch and watch it run',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: 'Launch your first agent',
      body: "Agents run your tasks in their own cloud devbox while you focus on other things. We'll go from an idea to a running agent in well under a minute. Here's the run we will set up together:",
      path: '/runs',
      preview: {
        rows: [
          { icon: 'prompt', label: 'Task', value: 'Write a Minesweeper game in Python' },
          { icon: 'agent', label: 'Agent', value: 'OpenCode' },
          { icon: 'model', label: 'Model', value: 'big-pickle: free, no key needed' },
        ],
      },
    },
    {
      id: 'open-dialog',
      kind: 'action',
      trigger: 'click',
      title: 'Open the launch dialog',
      body: 'The **New agent** button, up in the top-right, is where every run starts.',
      target: '[aria-label="New agent"]',
      placement: 'right',
    },
    {
      id: 'prompt',
      kind: 'action',
      trigger: 'input',
      title: 'Describe what you want done',
      body: "This prompt is the agent's first instruction, so write it like you would brief a teammate - a goal, not a step-by-step script. New here? Hit **Fill example** to drop in a sample, then make it your own.",
      example: 'Write a Minesweeper game in Python, then run it and show me the board',
      target: '[role="dialog"] [aria-label="Message input"]',
      placement: 'top',
    },
    {
      id: 'settings',
      kind: 'info',
      title: 'Model and settings live right here',
      body: 'Tucked under your prompt is a compact bar that controls how this agent runs. The defaults are chosen to just work - the next few stops walk through the pieces you can change.',
      target: '[role="dialog"] [data-testid="agent-settings-chip"]',
      placement: 'top',
    },
    {
      id: 'settings-agent-model',
      kind: 'info',
      title: 'Pick the agent and model',
      body: 'The left control shows the **model** this run will use. Open it to switch the **agent** harness and choose another model. Reasoning effort sits beside it when the agent supports it.',
      target: '[role="dialog"] [aria-label="Edit agent and model"]',
      placement: 'top',
    },
    {
      id: 'settings-key',
      kind: 'info',
      title: 'Set the provider key',
      body: 'The right pill is the **provider key** the agent signs in with. If Launch is greyed out, it is waiting on one - set it here, or add a new key without leaving the dialog.',
      target: '[role="dialog"] [aria-label="Select model provider key"]',
      placement: 'top',
    },
    {
      id: 'attachments',
      kind: 'info',
      title: 'Give it more to work with (optional)',
      body: '**Add context** is how you attach more for the agent to work with - a **GitHub repository** to work in, MCP servers, network policies, and more as your org enables them. You can also type **@** in the message to pull them in. Whatever you add shows up as a chip here. Add what is useful, or launch with none of it.',
      target: '[role="dialog"] [data-testid="add-context-button"]',
      placement: 'bottom',
    },
    {
      id: 'advanced-open',
      kind: 'action',
      trigger: 'click',
      title: 'Fine-tune under Advanced',
      body: 'Deeper, power-user knobs live under **Advanced** - expand it to take a look.',
      target: '[role="dialog"] [data-testid="agent-config-advanced-toggle"]',
      placement: 'top',
      // The launcher always opens with Advanced collapsed (the launch form sets
      // `advancedStartsCollapsed`), so this click is what expands it. Advancing
      // on the expanded state rather than on the click keeps the step honest if
      // the section is somehow already open, so it never strands the tour.
      advanceWhen:
        '[role="dialog"] [data-testid="agent-config-advanced-toggle"][aria-expanded="true"]',
    },
    {
      id: 'advanced',
      kind: 'info',
      title: 'Advanced settings',
      body: 'This section gathers the optional controls - the **blueprint** the devbox starts from, its **sandbox size**, extra **capabilities**, and **environment variables** and **secrets** the agent can read. The defaults work for most runs; reach in here when a task needs more.',
      target: '[role="dialog"] #agent-config-advanced-content',
      placement: 'top',
    },
    {
      id: 'use-free-model',
      kind: 'info',
      title: 'Launch free - no key needed',
      body: "Those Anthropic and OpenAI models each need a provider key. So you can launch right now without setting one up, we just switched this run to **OpenCode's big-pickle** - a free model that needs no key. Watch the key pill drop away and **Launch** light up.",
      target: '[role="dialog"] [data-testid="agent-settings-chip"]',
      placement: 'top',
      // Declaratively drive the host launch form to the key-less free combo: the
      // dialog observes these signals (see its MutationObserver) and selects the
      // agent + model for the user. Done here - after the provider-key segment
      // has been explained - so the final Launch step is reachable in a fresh
      // org with no keys configured. Switching the agent already cascades to
      // big-pickle as its default model; we name the model explicitly too so the
      // pick stays exact if that default ever changes.
      htmlSignals: {
        'data-tutorial-launch-agent': 'opencode',
        'data-tutorial-launch-model': 'opencode/big-pickle',
      },
    },
    {
      id: 'launch',
      kind: 'action',
      trigger: 'click',
      title: 'Launch it',
      body: 'Hit **Launch** (or just press Enter) and the agent spins up its devbox and gets to work. You land on its workspace to watch it live.',
      // The dialog footer's Launch button - not the composer's send icon button,
      // which shares the "Launch agent" aria-label and sits at the top.
      target: '[role="dialog"] [data-tutorial-id="launch-submit"]',
      placement: 'top',
    },
    {
      id: 'done',
      kind: 'info',
      title: 'And it is off',
      body: 'Your agent is now running and you have landed on its workspace. Take the next tour for a lap around the live stream, follow-ups, view tabs, and run details - or close this and watch it work.',
      // Purely descriptive closer: leave the workspace bright and clickable so
      // the user can start poking at their running agent without dismissing the
      // tour first.
      dim: false,
    },
  ],
  handoff: { tutorialName: 'agent-detail', label: 'Tour the agent workspace' },
};
